import { getIsStatus, getStatusTypeObject, toPostStatusEnum } from '@/Components/posts/status';
import type { RenderIfVisibleProps } from '@/Utility/RenderIfVisible';
import { useAsyncMemo } from '@/Utility/hooks';
import {
  ImageHubPagePlaceHolder,
  ImageMediaPlaceHolder,
  ImageZnglistPlaceHolder,
  ImageZnglyLogoWhite,
} from '@/assets/images';
import type { Forms_Pagination_Fragment$data } from '@/graphql/queries/Form.query';
import type { HubPages_Pagination_Fragment$data } from '@/graphql/queries/HubPage.query';
import type { Media_Query_Pagination_Fragment$data } from '@/graphql/queries/Media.query';
import type { Users_Pagination_Fragment$data } from '@/graphql/queries/User.query';
import {
  type FormConnection,
  type HubPageConnection,
  type Hub as HubType,
  MediaItemSizeEnum,
  PostStatusEnum,
  type StatusSettings,
  type ZnglistConnection,
  type ZnglyVideo,
} from '@/graphql/types';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { faImages, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Person } from '@mui/icons-material';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  type CardProps,
  Grid2,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SxProps } from '@mui/system';
import { useMemo } from 'react';
import type { CreatePostsLoaderProps, PostsLoaderTypeKey, PostsLoaderTypes } from '../loaders';
import { getImageBackgroundColor, getMediaItemUrl } from '../utils';
import { getCatalogMediaFeaturedImage, getDescription } from '../utils/catalog';

import styles from './posts-card.module.css';

type CatalogTypeNode = ZnglyVideo | PostsLoaderTypes['catalog']['paginationNodeType'];
type CatalogProps = {
  type: 'catalog';
  node: CatalogTypeNode;
  onClick?: (node: CatalogTypeNode) => void;
};

type ZnglistTypeNode = ZnglistConnection['edges'][number]['node'] | PostsLoaderTypes['znglist']['paginationNodeType'];
type ZnglistProps = {
  type: 'znglist';
  node: ZnglistTypeNode;
  onClick?: (node: ZnglistTypeNode) => void;
};

type HubPageTypeNode =
  | HubPageConnection['edges'][number]['node']
  | NonNullable<HubPages_Pagination_Fragment$data['hubPages']>['edges'][number]['node']
  | PostsLoaderTypes['hubPage']['paginationNodeType'];
type HubPageProps = {
  type: 'hubPage';
  node: HubPageTypeNode;
  onClick?: (node: HubPageTypeNode) => void;
};

type HubTypeNode = HubType | PostsLoaderTypes['hub']['paginationNodeType'];
type HubProps = {
  type: 'hub';
  node: HubTypeNode;
  onClick?: (node: HubTypeNode) => void;
};

type MediaTypeNode =
  | NonNullable<Media_Query_Pagination_Fragment$data['mediaItems']>['edges'][number]['node']
  | PostsLoaderTypes['media']['paginationNodeType'];
type MediaProps = {
  type: 'media';
  node: MediaTypeNode;
  onClick?: (node: MediaTypeNode) => void;
};

type FormTypeNode =
  | FormConnection['edges'][number]['node']
  | NonNullable<Forms_Pagination_Fragment$data['forms']>['edges'][number]['node']
  | PostsLoaderTypes['form']['paginationNodeType'];
type FormProps = {
  type: 'form';
  node: FormTypeNode;
  onClick?: (node: FormTypeNode) => void;
};

type UserTypeNode =
  | NonNullable<Users_Pagination_Fragment$data['users']>['edges'][number]['node']
  | PostsLoaderTypes['user']['paginationNodeType'];
type UserProps = {
  type: 'user';
  node: UserTypeNode;
  onClick?: (node: UserTypeNode) => void;
};

type PostsUnion = CatalogProps | ZnglistProps | HubPageProps | HubProps | MediaProps | FormProps | UserProps;

type PostsPick<T extends PostsLoaderTypeKey = never> = T extends never ? PostsUnion : Extract<PostsUnion, { type: T }>;

// props for the card component
type BaseProps<T extends PostsLoaderTypeKey, P extends {}> = {
  fallback?: boolean;
  showHref?: boolean;
  disabled?: boolean;
  showAdditionalInfo?: boolean;
  idx?: number;
  cardProps?: Omit<CardProps, 'children'>;
  /** Default 'top' */
  imagePlacement?: 'top' | 'left';
  /** Default 140 */
  imageHeight?: number;
} & PostsPick<T> &
  P;

type PropsPick<T extends PostsLoaderTypeKey = never, P extends {} = Record<string, unknown>> = CreatePostsLoaderProps<
  T,
  BaseProps<T, P>
>;

export type { PostsPick as PickFromPostCardTypes };

const AdditionalInfo = <T extends PostsLoaderTypeKey>(props: {
  node: PostsPick<T>['node'];
  type: PostsPick<T>['type'];
}) => {
  const status = useMemo(() => {
    if (props?.node && 'statusSettings' in props.node) {
      return getStatusTypeObject(props?.node?.statusSettings as StatusSettings);
    }
    if (props?.node && 'status' in props.node) {
      return getStatusTypeObject({ status: toPostStatusEnum(props?.node?.status) });
    }
    return null;
  }, [props?.node, props?.type]);

  const tagContainerStyles: SxProps = {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    '&:hover': { cursor: 'help' },
    svg: { fontSize: 18 },
    p: { fontSize: 12, marginLeft: 1, whiteSpace: 'normal', overflow: 'hidden', textOverflow: 'ellipsis' },
  };

  return (
    <Stack
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        border: 1,
        borderColor: 'divider',
        padding: 1,
        borderRadius: 1,
      }}
    >
      {status && (
        <Box sx={tagContainerStyles}>
          {/* Status tag */}
          {status?.icon}
          <Typography variant="body2" sx={{ marginLeft: '4px', fontWeight: 'bold' }}>
            {status?.label}
          </Typography>
        </Box>
      )}
      {props?.node && 'author' in props.node && props?.node?.author?.node?.name && (
        <Tooltip title={props?.node?.author?.node?.name}>
          <Box sx={tagContainerStyles}>
            {/* Author tag */}
            <Person fontSize="small" />
            <Typography variant="body2" sx={{ marginLeft: '4px' }}>
              {props?.node?.author?.node?.name}
            </Typography>
          </Box>
        </Tooltip>
      )}
    </Stack>
  );
};

const getHeight = (type: PostsLoaderTypeKey, additionalInfo = false) => {
  if (type === 'catalog') {
    if (additionalInfo) return 300;
    return 250;
  }
  if (type === 'znglist') {
    if (additionalInfo) return 200;
    return 200;
  }
  if (type === 'hubPage') {
    if (additionalInfo) return 300;
    return 200;
  }
  if (type === 'hub') {
    if (additionalInfo) return 300;
    return 200;
  }
  if (type === 'media') {
    return 200;
  }
};

type CardComponentProps<T extends PostsLoaderTypeKey> = PropsPick<
  T,
  {
    draggableProps?: ReturnType<typeof useDraggable> & { style?: React.CSSProperties };
  }
>;

const CardComponent = <T extends PostsLoaderTypeKey>({
  type,
  node,
  onClick,
  fallback,
  showHref,
  disabled,
  ...props
}: CardComponentProps<T>) => {
  const draggableProps = useMemo(() => {
    if (!props?.draggableProps) {
      return {};
    }

    const { isDragging } = props.draggableProps;

    return {
      ref: props?.draggableProps?.setNodeRef,
      ...props?.draggableProps?.listeners,
      ...props?.draggableProps?.attributes,
      style: {
        ...props?.draggableProps?.style,
        opacity: isDragging ? 0 : undefined,
        transform: CSS.Translate.toString(props?.draggableProps?.transform),
      },
    };
  }, [props?.draggableProps]);

  const { featuredImage, desc, title, hasAiResult } = useMemo(() => {
    if (type === 'catalog') {
      const featuredImage = getCatalogMediaFeaturedImage(node?.znglyContent, MediaItemSizeEnum.Medium);
      const { aiExtractiveAnswer, description, hasAiResult } = getDescription(node as ZnglyVideo);
      const desc = !!aiExtractiveAnswer ? aiExtractiveAnswer : description;
      return { featuredImage, desc, title: node?.title, hasAiResult };
    }
    if (type === 'znglist') {
      const znglistContents = node?.znglist?.znglyContents;
      const firstZnglyVideo = znglistContents?.[0];

      return {
        featuredImage: !!firstZnglyVideo
          ? getCatalogMediaFeaturedImage(firstZnglyVideo?.znglyContent, MediaItemSizeEnum.Medium)
          : ImageZnglistPlaceHolder,
        desc: null,
        title: node?.title,
        hasAiResult: false,
      };
    }
    if (type === 'hubPage') {
      const desc = node?.description ?? '';

      let featuredImage = ImageHubPagePlaceHolder;
      if (node?.connectionHpToCs?.edges?.length) {
        const firstCs = node?.connectionHpToCs?.edges?.[0]?.node;
        if (firstCs?.catalog?.node?.znglyContent) {
          const img =
            getCatalogMediaFeaturedImage(firstCs?.catalog?.node?.znglyContent, MediaItemSizeEnum.Thumbnail) ?? '';
          if (img) featuredImage = img;
        }
      }

      return { featuredImage, desc, title: node?.title, hasAiResult: false };
    }
    if (type === 'media') {
      let mediaItemImage = getMediaItemUrl(node, MediaItemSizeEnum.Large);
      if (!node?.mimeType?.includes?.('image')) mediaItemImage = ImageMediaPlaceHolder;

      return { featuredImage: mediaItemImage, desc: '', title: node?.title, hasAiResult: false };
    }

    if (type === 'form') {
      return {
        featuredImage: node?.imageUrl ?? ImageZnglyLogoWhite,
        title: node?.content ?? node?.description ?? '',
        desc: node?.title,
        hasAiResult: false,
      };
    }

    if (type === 'user') {
      return {
        featuredImage: node?.avatar?.url ?? '',
        title: node?.email ?? '',
        desc: node?.username ?? '',
        hasAiResult: false,
      };
    }

    return { featuredImage: '', desc: '', hasAiResult: false };
  }, [node, type]);

  const backgroundColor = useAsyncMemo(async () => {
    return await getImageBackgroundColor(featuredImage ?? '');
  }, [featuredImage, node?.id]);

  const onClickHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (!onClick) return;
    e.preventDefault();
    if (disabled) return;

    // @ts-ignore - for some reason the type inference does not want to work properly
    onClick?.(node);
  };

  const cardHeight = useMemo(() => {
    return getHeight(type, props?.showAdditionalInfo);
  }, [type, props?.showAdditionalInfo]);

  return (
    <Card
      sx={{
        margin: 1,
        height: `${cardHeight}px`,
        ...(node &&
          'status' in node &&
          getIsStatus(node?.status, PostStatusEnum.Draft) && {
            // Apply red border condition directly to Card instead of CardActionArea
            border: '1px solid red',
          }),
      }}
      {...draggableProps}
      {...props?.cardProps}
    >
      <CardActionArea
        {...(showHref &&
          (node as PostsPick<'catalog'>['node'])?.link && {
            href: (node as PostsPick<'catalog'>['node'])?.link ?? '#',
          })}
        onClick={onClickHandler}
        sx={{
          display: 'flex',
          flexDirection: props?.imagePlacement === 'left' ? 'row' : 'column',
          ...(disabled && {
            opacity: 0.4,
            backgroundColor: 'rgba(0, 0, 0, 5%)',
          }),
        }}
      >
        <div style={{ position: 'relative' }}>
          {fallback ? (
            <Skeleton variant="rectangular" height={140} />
          ) : (
            <CardMedia
              component="img"
              image={fallback ? '' : (featuredImage ?? '')}
              alt={title ?? ''}
              sx={{
                height: props?.imageHeight ?? 140,
                objectFit: 'contain',
                backgroundColor: backgroundColor ?? '#d2d2d2',
              }}
            />
          )}
          {type === 'catalog' && (
            <div className={styles.thumbnailMediaContainer}>
              <span>
                <FontAwesomeIcon icon={faImages} /> {node?.znglyContent?.contentMediaName}
              </span>
            </div>
          )}
          {type === 'catalog' && hasAiResult && (
            <Tooltip title={'This result came from AI'} placement="top" enterDelay={300}>
              <div className={styles.aiSearchIcon}>
                <FontAwesomeIcon icon={faWandMagicSparkles} />
              </div>
            </Tooltip>
          )}
        </div>
        <CardContent sx={{ width: '100%' }}>
          <Tooltip title={title} placement="top" enterDelay={750}>
            <Typography gutterBottom variant="body2" component="div" noWrap fontWeight={600}>
              {title}
            </Typography>
          </Tooltip>
          <Tooltip title={desc} placement="top" enterDelay={750}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                display: '-webkit-box',
                height: '2.5rem',
              }}
            >
              {desc}
            </Typography>
          </Tooltip>
          {props?.showAdditionalInfo && <AdditionalInfo type={type} node={node} />}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export const PostCardCatalogDndPrefix = 'grid-item-catalog-';
export const PostCardZnglistDndPrefix = 'grid-item-znglist-';
export const PostCardHubPageDndPrefix = 'grid-item-hubPage-';
export const PostCardHubDndPrefix = 'grid-item-hub-';
export const PostCardMediaDndPrefix = 'grid-item-media-';
export const PostCardFormDndPrefix = 'grid-item-form-';

const WithDraggable = <T extends PostsLoaderTypeKey>(props: PropsPick<T>) => {
  const draggableProps = useDraggable({
    id: `grid-item-${props?.type}-${props?.node.id}`,
    data: props?.node,
  });

  // @ts-ignore
  return <CardComponent<T> {...props} draggableProps={draggableProps} />;
};

export type PostCardProps<T extends PostsLoaderTypeKey> = PropsPick<T, { useDraggable?: boolean }>;
const PostCard = <T extends PostsLoaderTypeKey>({ useDraggable, ...props }: PostCardProps<T>) => {
  if (useDraggable === true) {
    // @ts-ignore
    return <WithDraggable<T> {...(props as PropsPick<T>)} />;
  }
  // @ts-ignore
  return <CardComponent<T> {...(props as CardComponentProps<T>)} />;
};

// grid render if visible props
export const postCardRIVProps = <T extends PostsLoaderTypeKey>(
  node: PostsPick<T>['node'],
  type: T,
  idx: number,
  useDraggable = false,
  showAdditionalInfo = false
): Partial<RenderIfVisibleProps> => {
  return {
    defaultHeight: getHeight(type, showAdditionalInfo),
    // initialVisible: true,
    stayRendered: true,
    fallback: (
      // @ts-ignore
      <PostCard<T>
        type={type}
        node={node}
        idx={idx}
        useDraggable={useDraggable}
        showAdditionalInfo={showAdditionalInfo}
      />
    ),
    rootElement: <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }} key={node?.id} />,
  };
};

export default PostCard;
